// Контейнер 1-битного растра и упаковка бит (T-028, слой 1 контракта печати).
//
// Формат зафиксирован в `printerAdapter.ts`: длина `bits` равна
// `ceil(widthPx / 8) * heightPx`, единица — ЧЁРНАЯ точка. Выравнивание строки
// по байту не для красоты: TSPL-команда `BITMAP` задаёт ширину в БАЙТАХ, и строка,
// не кратная восьми битам, поехала бы на каждой следующей строке — этикетка
// напечаталась бы лесенкой (см. T-029).
//
// Инверсию (у части языков команд единица означает белое) делает слой 2. Здесь
// значение бита одно и описано в контракте: два разных смысла у одного бита
// в двух слоях — это способ однажды напечатать негатив и долго искать почему.

import type { LabelBitmap } from '../printerAdapter';

/** Растр во время рисования: те же данные, но `bits` ещё можно менять. */
export type MutableBitmap = {
  readonly widthPx: number;
  readonly heightPx: number;
  readonly dotsPerMm: number;
  readonly bits: Uint8Array;
};

/** Байт на строку: ширина, округлённая вверх до целого байта. */
export function rowStride(widthPx: number): number {
  return Math.ceil(widthPx / 8);
}

export function createBitmap(widthPx: number, heightPx: number, dotsPerMm: number): MutableBitmap {
  if (widthPx <= 0 || heightPx <= 0) {
    throw new RangeError(`Растр ${widthPx}x${heightPx}: размеры должны быть положительными`);
  }
  return {
    widthPx,
    heightPx,
    dotsPerMm,
    bits: new Uint8Array(rowStride(widthPx) * heightPx),
  };
}

/**
 * Зажечь точку. Координаты вне растра игнорируются молча — и это осознанно.
 *
 * Рисование текста у края этикетки постоянно даёт координаты за границей: у глифа
 * есть отрицательный левый вынос, у диакритики — верхний. Бросать исключение
 * значило бы падать на этикетке с буквой «Й» в первой позиции; обрезка по границе
 * это ровно то поведение, которого ждёт вёрстка.
 */
export function setPixel(bitmap: MutableBitmap, x: number, y: number): void {
  if (x < 0 || y < 0 || x >= bitmap.widthPx || y >= bitmap.heightPx) {
    return;
  }
  const index = y * rowStride(bitmap.widthPx) + (x >> 3);
  // Старший бит — ЛЕВЫЙ пиксель: так читают растр и TSPL, и PBM, и глаз в дампе.
  bitmap.bits[index] = (bitmap.bits[index] ?? 0) | (0b1000_0000 >> (x & 7));
}

/** Погасить точку — белый текст по чёрной полосе ярлыка. Вне растра — молча, как `setPixel`. */
export function clearPixel(bitmap: MutableBitmap, x: number, y: number): void {
  if (x < 0 || y < 0 || x >= bitmap.widthPx || y >= bitmap.heightPx) {
    return;
  }
  const index = y * rowStride(bitmap.widthPx) + (x >> 3);
  bitmap.bits[index] = (bitmap.bits[index] ?? 0) & ~(0b1000_0000 >> (x & 7));
}

/** Залить прямоугольник чёрным: полоса, рамки, черта у «Годен до». Правый/нижний край не включается. */
export function fillRect(
  bitmap: MutableBitmap,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): void {
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      setPixel(bitmap, x, y);
    }
  }
}

/** Горизонтальный пунктир толщиной `thickness`: штрих `dash`, пробел `gap`. */
export function dashedLine(
  bitmap: MutableBitmap,
  x0: number,
  x1: number,
  y: number,
  thickness: number,
  dash: number,
  gap: number,
): void {
  for (let x = x0; x < x1; x += dash + gap) {
    fillRect(bitmap, x, y, Math.min(x + dash, x1), y + thickness);
  }
}

export function getPixel(bitmap: MutableBitmap, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= bitmap.widthPx || y >= bitmap.heightPx) {
    return false;
  }
  const index = y * rowStride(bitmap.widthPx) + (x >> 3);
  return ((bitmap.bits[index] ?? 0) & (0b1000_0000 >> (x & 7))) !== 0;
}

/** Сколько точек зажжено. Нужен тестам: «кириллица не пустая» проверяется числом. */
export function countBlack(bitmap: MutableBitmap): number {
  let total = 0;
  for (const byte of bitmap.bits) {
    // popcount восьми бит без таблиц.
    let value = byte;
    while (value !== 0) {
      value &= value - 1;
      total += 1;
    }
  }
  return total;
}

/** Заморозить результат в тип контракта. */
export function toLabelBitmap(bitmap: MutableBitmap): LabelBitmap {
  return {
    widthPx: bitmap.widthPx,
    heightPx: bitmap.heightPx,
    dotsPerMm: bitmap.dotsPerMm,
    bits: bitmap.bits,
  };
}

/**
 * Сериализация в PBM (P1) — текстовый формат, где точка это символ.
 *
 * Снапшоты растра хранятся именно так, а не в base64: диф по base64 показывает,
 * что «что-то поменялось», а диф по PBM показывает ЧТО именно — глазами видно,
 * что съехала строка или пропала буква. Ревьюер обязан иметь возможность прочитать
 * снапшот, иначе снапшот-тест превращается в кнопку «принять как есть».
 */
export function toPbm(bitmap: MutableBitmap): string {
  const lines: string[] = [`P1`, `${bitmap.widthPx} ${bitmap.heightPx}`];
  for (let y = 0; y < bitmap.heightPx; y += 1) {
    let row = '';
    for (let x = 0; x < bitmap.widthPx; x += 1) {
      row += getPixel(bitmap, x, y) ? '1' : '0';
    }
    lines.push(row);
  }
  return lines.join('\n') + '\n';
}

/** Обратная операция — читает снапшот. Нужна тесту, сверяющему PBM с растром. */
export function fromPbm(text: string): MutableBitmap {
  const lines = text.trim().split('\n');
  const header = lines[1]?.trim().split(/\s+/) ?? [];
  const widthPx = Number(header[0]);
  const heightPx = Number(header[1]);
  const bitmap = createBitmap(widthPx, heightPx, 8);
  for (let y = 0; y < heightPx; y += 1) {
    const row = lines[2 + y] ?? '';
    for (let x = 0; x < widthPx; x += 1) {
      if (row[x] === '1') {
        setPixel(bitmap, x, y);
      }
    }
  }
  return bitmap;
}
